import { useEffect, useState } from 'react'
import { useOnboardingStore } from '../stores/onboardingStore'

const debugLog = []
const subscribers = new Set()

export function dlog(level, ...args) {
  const ts = new Date().toLocaleTimeString('en-US', { hour12: false })
  const text = args
    .map((a) => {
      if (typeof a === 'string') return a
      try { return JSON.stringify(a, null, 0).slice(0, 300) }
      catch { return String(a) }
    })
    .join(' ')
  const entry = { ts, level, text, id: Date.now() + Math.random() }
  debugLog.push(entry)
  if (debugLog.length > 200) debugLog.shift()
  subscribers.forEach((cb) => cb([...debugLog]))
}

function serializeError(err) {
  if (!err) return 'unknown error'
  if (typeof err === 'string') return err
  if (err instanceof Error) {
    return `${err.name || 'Error'}: ${err.message}${err.stack ? '\n' + err.stack.split('\n').slice(0, 3).join('\n') : ''}`
  }
  try { return JSON.stringify(err).slice(0, 500) }
  catch { return String(err) }
}

// Capture console.error, window.onerror, unhandledrejection into the dlog
// buffer. Guarded so the instrumentation only attaches once per page load.
if (typeof window !== 'undefined' && !window.__briteDebugHooked) {
  window.__briteDebugHooked = true
  const originalConsoleError = window.console.error.bind(window.console)
  window.console.error = (...args) => {
    try {
      const msg = args
        .map((a) =>
          a instanceof Error
            ? serializeError(a)
            : typeof a === 'string'
              ? a
              : (() => {
                  try { return JSON.stringify(a).slice(0, 300) }
                  catch { return String(a) }
                })(),
        )
        .join(' ')
      dlog('error', '[console]', msg)
    } catch {}
    originalConsoleError(...args)
  }
  window.addEventListener('error', (ev) => {
    dlog(
      'error',
      '[window.error]',
      ev.message || serializeError(ev.error),
      ev.filename ? `(${ev.filename}:${ev.lineno})` : '',
    )
  })
  window.addEventListener('unhandledrejection', (ev) => {
    dlog('error', '[unhandledrejection]', serializeError(ev.reason))
  })

  // Capture failed image loads — IMG error events don't bubble, use capture.
  document.addEventListener(
    'error',
    (ev) => {
      const t = ev.target
      if (t && t.tagName === 'IMG') {
        dlog('error', '[img]', 'Image failed to load:', t.src || '(no src)')
      }
    },
    true,
  )

  // Capture failing fetch responses + thrown fetches.
  const origFetch = window.fetch
  if (typeof origFetch === 'function') {
    window.fetch = async (...args) => {
      const [url, opts] = args
      const method = String(opts?.method || 'GET').toUpperCase()
      const urlStr =
        typeof url === 'string' ? url : url?.url || String(url)
      try {
        const res = await origFetch(...args)
        if (!res.ok) {
          try {
            const cloned = res.clone()
            const bodyText = await cloned.text()
            let bodyPretty = bodyText
            try {
              bodyPretty = JSON.stringify(JSON.parse(bodyText), null, 2)
            } catch {}
            window.__briteLastError = {
              ts: new Date().toISOString(),
              method,
              url: urlStr,
              status: res.status,
              statusText: res.statusText,
              body: bodyPretty,
            }
            dlog(
              'error',
              '[fetch]',
              `${method} ${urlStr} → ${res.status} ${res.statusText}\n${bodyPretty.slice(0, 1500)}`,
            )
          } catch {
            dlog(
              'error',
              '[fetch]',
              `${method} ${urlStr} → ${res.status} ${res.statusText} (body unreadable)`,
            )
          }
        }
        return res
      } catch (err) {
        dlog(
          'error',
          '[fetch]',
          `${method} ${urlStr} threw:`,
          err?.message || String(err),
        )
        throw err
      }
    }
  }
}

export default function DebugOverlay() {
  const [logs, setLogs] = useState(debugLog)
  const [open, setOpen] = useState(true)

  useEffect(() => {
    subscribers.add(setLogs)
    return () => subscribers.delete(setLogs)
  }, [])

  const copyAll = (e) => {
    e.stopPropagation()
    const text = debugLog
      .map((l) => `${l.ts} [${l.level}] ${l.text}`)
      .join('\n')
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {})
    }
  }
  const clearAll = (e) => {
    e.stopPropagation()
    debugLog.length = 0
    subscribers.forEach((cb) => cb([]))
  }

  const nukeCache = async (e) => {
    e.stopPropagation()
    try {
      try { localStorage.clear() } catch {}
      try { sessionStorage.clear() } catch {}
      try {
        if ('caches' in window) {
          const names = await caches.keys()
          await Promise.all(names.map((n) => caches.delete(n)))
        }
      } catch {}
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all(regs.map((r) => r.unregister()))
        }
      } catch {}
    } finally {
      const params = new URLSearchParams(window.location.search)
      params.set('t', String(Date.now()))
      window.location.href =
        window.location.pathname + '?' + params.toString()
    }
  }

  const fillAll = (e) => {
    e.stopPropagation()
    const store = useOnboardingStore.getState()
    const update = store.updateFormData

    // Step 1 — Practice details (DTC)
    update('details', {
      practiceName: 'Kashif Aesthetics',
      practiceType: 'dtc',
      logo: null,
    })

    // Step 2 — Billing
    update('billing', {
      pricingAcknowledged: true,
      payForNonSubscriptionMedications: true,
      payForShippingCharges: true,
      practiceMarkup: 10,
      labOrderPayer: 'Practice',
    })

    // Step 3 — Managed Billing (acknowledged + both packages)
    update('managedBilling', {
      acknowledged: true,
      packages: {
        female: {
          internalName: 'Female BHRT package',
          displayName: 'Female BHRT package',
          monthlyPrice: 200,
          annualPrice: 2000,
        },
        male: {
          internalName: 'Male BHRT package',
          displayName: 'Male BHRT package',
          monthlyPrice: 250,
          annualPrice: 2500,
        },
      },
    })

    // Step 4 — Owners (Kashif as the owner — uses het+ aliasing for testing)
    update('owners', {
      owners: [
        {
          fullName: 'Kashif Khan',
          email: 'het+kashif@brite.live',
          phoneNo: '5551234567',
        },
      ],
    })

    // Step 5 — Locations (single primary)
    update('locations', {
      locations: [
        {
          id: 'loc-1',
          addressLine1: '500 Madison Ave',
          addressLine2: 'Suite 1200',
          city: 'New York',
          state: 'NY',
          zip: '10022',
          country: 'United States',
          lat: 40.7613,
          lng: -73.9719,
          isPrimary: true,
        },
      ],
    })

    // Step 6 — Network+ enabled
    update('networkplus', { isMemberPlusMember: true })

    // Step 7 — Providers (DTC + N+ has NO providers — CAP handles
    // everything; backend refine allows empty providers when N+ is on)
    update('providers', {
      providers: [],
    })

    // Step 8 — Kits (one demo kit, recipient = Kashif at primary location)
    update('kits', {
      kits: [
        {
          quantity: 1,
          recipientName: 'Kashif Khan',
          recipientEmail: 'het+kashif-kit@brite.live',
          locationIds: ['loc-1'],
          customShipTo: null,
          isCustomRecipient: true,
          kitSource: 'custom',
        },
      ],
    })

    // Step 9 — Teams (front desk + ops, with real permissions so the
    // step doesn't flag needs-attention)
    update('teams', {
      teams: [
        {
          fullName: 'Maria Lopez',
          email: 'het+maria@brite.live',
          phoneNo: '5552223333',
          permissions: ['assistant'],
          locationIds: ['loc-1'],
          jobDescription: 'Front desk',
        },
        {
          fullName: 'James Patel',
          email: 'het+james@brite.live',
          phoneNo: '5554445555',
          permissions: ['finance', 'analytics'],
          locationIds: ['loc-1'],
          jobDescription: 'Operations',
        },
      ],
    })

    // Step 11 — Agreements: pre-set CAP gate to owner-NOT-CAP with CAP
    // info filled in. agreementsAcknowledged left false so the checkbox
    // exercises the real flow. CAP email uses het+cap aliasing for testing.
    update('agreements', {
      isOwnerCap: false,
      capName: 'Dr. Sarah Chen',
      capEmail: 'het+cap@brite.live',
      capPhone: '5556667777',
    })

    dlog(
      'success',
      '[fillAll] Kashif profile: DTC + N+ + Managed Billing + owner-not-CAP — payment + signatures still required',
    )
    if (typeof store.goToStep === 'function') store.goToStep(11)
  }

  const showLastError = (e) => {
    e.stopPropagation()
    const last = window.__briteLastError
    if (!last) {
      dlog('warn', '[lastError] no failed request captured yet')
      return
    }
    const text = `${last.ts} ${last.method} ${last.url} → ${last.status} ${last.statusText}\n${last.body}`
    dlog('error', '[lastError]', text)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {})
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 100,
        right: 16,
        width: 400,
        maxHeight: open ? 360 : 32,
        background: 'rgba(0,0,0,0.92)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 8,
        zIndex: 99999,
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
        fontSize: 11,
        color: '#e5e5e5',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: '6px 10px',
          background: 'rgba(255,255,255,0.05)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'rgba(255,255,255,0.6)',
          gap: 8,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Debug ({logs.length})</span>
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            onClick={copyAll}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.7)',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Copy all
          </button>
          <button
            type="button"
            onClick={clearAll}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.7)',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={fillAll}
            title="Fill all steps with test data (skips payment + signature)"
            style={{
              background: 'transparent',
              border: '1px solid rgba(184,232,216,0.55)',
              color: '#B8E8D8',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Fill all
          </button>
          <button
            type="button"
            onClick={showLastError}
            title="Show + copy the last failed backend response (status, headers, body)"
            style={{
              background: 'transparent',
              border: '1px solid rgba(212,169,104,0.55)',
              color: '#d4a968',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Last error
          </button>
          <button
            type="button"
            onClick={nukeCache}
            title="Wipe localStorage, sessionStorage, caches, and service workers, then reload"
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,138,122,0.55)',
              color: '#ff8a7a',
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Nuke cache
          </button>
          <span>{open ? '−' : '+'}</span>
        </span>
      </div>
      {open && (
        <div
          style={{
            maxHeight: 320,
            overflowY: 'auto',
            padding: '6px 10px',
          }}
        >
          {logs.length === 0 && (
            <div style={{ color: 'rgba(255,255,255,0.4)', padding: '8px 0' }}>
              No logs yet…
            </div>
          )}
          {logs.map((l) => (
            <div
              key={l.id}
              style={{
                padding: '3px 0',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                color:
                  l.level === 'error'
                    ? '#ff8a7a'
                    : l.level === 'warn'
                      ? '#d4a968'
                      : l.level === 'success'
                        ? '#5DCDB6'
                        : '#cccccc',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>{l.ts}</span>{' '}
              {l.text}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
